import { useEffect } from 'react';
import i18next from "i18next";

export const usePageName = (urlPath?: string) => {
  useEffect(() => {
    if (urlPath) {
      document.title = getPageTitle(urlPath);
    }
  }, [urlPath]);
};

const getPageTitle = (url: string) => {
  const lowerCasedUrl = url.toLowerCase();
  switch (lowerCasedUrl) {
    // Auth
    case '/login':
      return 'Team Today - Login';
    case '/register':
      return 'Team Today - Register';
    case '/forgot-password':
      return 'Team Today - Forgot password';
    case '/reset-password':
      return 'Team Today - Reset password';
    case '/complete-registration':
      return 'Team Today - Complete registration';
    case '/404':
      return 'Team Today - 404';
    case '/onboarding':
      return 'Team Today - Onboarding';

    // Whereabouts
    case '/whereabouts/team':
      return 'Team Today - Team view';
    case '/whereabouts/company':
    case '/whereabouts/company/all':
      return 'Team Today - Company whereabouts';
    case '/whereabouts/company/workgroups':
      return 'Team Today - Company workgroups';
    case '/whereabouts/company/departments':
      return 'Team Today - Departments';
    case '/whereabouts/company/lockers':
      return 'Team Today - Lockers';
    case '/whereabouts/my-summary':
      return 'Team Today - My whereabouts summary';
    case '/whereabouts/floor-planner':
      return 'Team Today - Floor planner';
    case '/whereabouts/office-view':
      return 'Team Today - Office view';
    case '/whereabouts/meeting-rooms':
      return 'Team Today - Meeting rooms';
    case '/whereabouts/meeting-rooms/my-bookings':
      return 'Team Today - My room bookings';
    case '/whereabouts/meeting-rooms/hospitality-orders':
      return 'Team Today - Hospitality orders';

    // Holidays
    case '/holidays':
      return 'Team Today - Your leave';
    case '/holidays/add':
      return 'Team Today - Add Leave';
    case '/holidays/leave-planner':
      return 'Team Today - Leave planner';
    case '/holidays/view-approvals':
      return 'Team Today - Leave approvals';
    case '/holidays/reports':
      return 'Team Today - Leave reports';
    case '/holidays/me':
      return 'Team Today - My leave';
    case '/holidays/settings/allowance':
      return 'Team Today - Leave allowance settings';

    // Work planner
    case '/work-planner':
      return `Team Today - ${i18next.t('work-planner.work-planner')}`;

    // Meeting rooms
    case '/meeting-rooms':
      return 'Team Today - Meeting rooms';
    case '/meeting-rooms/my-bookings':
      return 'Team Today - My room bookings';
    case '/meeting-rooms/hospitality-orders':
      return 'Team Today - Hospitality orders';

    // Settings
    case '/manage':
      return 'Team Today - Settings';
    case '/manage/global-settings':
      return 'Team Today - Settings';
    case '/manage/teams-and-users':
      return 'Team Today - Team management';
    case '/manage/departments':
      return 'Team Today - Departments';
    case '/manage/assign-users':
      return 'Team Today - Assign users';
    case '/manage/company-holidays':
      return 'Team Today - Company leave';
    case '/manage/preferences':
      return 'Team Today - Personal preferences';
    case '/manage/payments':
      return 'Team Today - Payments';
    case '/manage/whereabouts':
    case '/manage/custom-whereabouts':
      return 'Team Today - Custom whereabouts';
    case '/manage/holidays':
      return 'Team Today - Leave settings';
    case '/manage/users':
      return 'Team Today - Manage users';
    case '/manage/workgroups':
      return 'Team Today - Workgroups';
    case '/manage/user-sync':
      return 'Team Today - User sync';

    // Offices
    case '/manage/offices':
      return 'Team Today - Offices';
    case '/manage/hot-desking':
    case '/manage/advance-hot-desking-setup':
      return 'Team Today - Hot desking setup';

    // Meeting room management
    case '/manage/meeting-rooms':
      return 'Team Today - Manage meeting rooms';
    case '/manage/meeting-rooms/add':
      return 'Team Today - Add meeting room';

    // Reporting
    case '/manage/reporting':
      return 'Team Today - Reporting';
    case '/manage/reporting/office':
      return 'Team Today - Office usage report';
    case '/manage/reporting/whereabouts':
      return 'Team Today - Whereabouts report';
    case '/manage/reporting/holidays':
      return 'Team Today - Leave usage report';
    case '/manage/reporting/office-checkin':
      return 'Team Today - Office check-in report';
    case '/manage/reporting/unknown-whereabouts':
      return 'Team Today - Unknown whereabouts report';
    case '/manage/reporting/key-responsibilities':
      return 'Team Today - Key responsibilities report';
    case '/manage/reporting/line-reports':
      return 'Team Today - Line reports';
    case '/manage/reporting/desk-utilization':
      return 'Team Today - Desk utilization report';
    case '/manage/reporting/car-parking-utilization':
      return 'Team Today - Car park usage report';
    case '/manage/user/whereabouts-summary':
      return 'Team Today - User whereabouts summary';
  }

  // Dynamic routes with parameters
  if (lowerCasedUrl.startsWith('/manage/company/')) {
    return 'Team Today - Company settings';
  } else if (lowerCasedUrl.startsWith('/manage/team/')) {
    return 'Team Today - Team settings';
  } else if (lowerCasedUrl.startsWith('/manage/user/')) {
    return 'Team Today - User settings';
  } else if (lowerCasedUrl.startsWith('/manage/offices/')) {
    return 'Team Today - Office settings';
  } else if (lowerCasedUrl.startsWith('/manage/meeting-rooms/edit/')) {
    return 'Team Today - Edit meeting room';
  } else if (lowerCasedUrl.startsWith('/manage/hot-desking/')) {
    return 'Team Today - Hot desking setup';
  } else if (lowerCasedUrl.startsWith('/manage/custom-whereabouts/')) {
    return 'Team Today - Custom whereabouts';
  } else if (lowerCasedUrl.startsWith('/whereabouts/team/')) {
    return 'Team Today - Team view';
  } else if (lowerCasedUrl.startsWith('/whereabouts/tag/')) {
    return 'Team Today - Workgroup view';
  } else if (lowerCasedUrl.startsWith('/whereabouts/company/team/')) {
    return 'Team Today - Team view';
  } else if (lowerCasedUrl.startsWith('/whereabouts/company/tag/')) {
    return 'Team Today - Workgroup view';
  } else if (lowerCasedUrl.startsWith('/whereabouts/company/departments/')) {
    return 'Team Today - Department view';
  } else if (lowerCasedUrl.startsWith('/whereabouts/visitor-booking/')) {
    return 'Team Today - Visitor booking';
  } else if (lowerCasedUrl.startsWith('/holidays/')) {
    return 'Team Today - Leave';
  } else if (lowerCasedUrl.startsWith('/work-planner/event')) {
    return 'Team Today - Edit team event / project';
  } else {
    return 'Team Today';
  }
}
